import { __read, __assign } from '../node_modules/tslib/tslib.es6.esm.js';
import * as React from 'react';
import '../utils/maskTypes.esm.js';
import { removeAllNonDigitCharsAndLeadingZeroes } from '../utils/utils.esm.js';

var INITIAL_PAGINATION_STATE = {
    currentPage: 1,
    currentPageInput: '1',
    numPerPage: 10,
    numPerPageInput: '10',
    text: '0',
    totalEntries: 0,
};
var NvPaginationContext = React.createContext({});
var NvPaginationContextProvider = NvPaginationContext.Provider;
var useNvPagination = function () {
    var _a = __read(React.useState(INITIAL_PAGINATION_STATE), 2), pagination = _a[0], setPagination = _a[1];
    var currentPage = pagination.currentPage, currentPageInput = pagination.currentPageInput, numPerPage = pagination.numPerPage, numPerPageInput = pagination.numPerPageInput, totalEntries = pagination.totalEntries;
    var resetPagination = React.useCallback(function () {
        setPagination(INITIAL_PAGINATION_STATE);
    }, [setPagination]);
    var goBackToPageOne = React.useCallback(function () {
        setPagination(function (pagination) {
            var newPagination = __assign(__assign({}, pagination), { currentPage: INITIAL_PAGINATION_STATE.currentPage, currentPageInput: INITIAL_PAGINATION_STATE.currentPageInput });
            return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
        });
    }, [setPagination]);
    var getNumPages = function () {
        return Math.ceil(totalEntries / numPerPage);
    };
    var getIsLeftChevronActive = function () {
        return currentPage > 1;
    };
    var getIsRightChevronActive = function () {
        return currentPage < getNumPages();
    };
    var getPaginationText = function (paginationValues) {
        var endLength = paginationValues.numPerPage * paginationValues.currentPage;
        var maxNumber = endLength > paginationValues.totalEntries ? paginationValues.totalEntries : endLength;
        var startLength = endLength - paginationValues.numPerPage + 1;
        if (!paginationValues.totalEntries) {
            return "0";
        }
        return "".concat(startLength, "-").concat(maxNumber, " of ").concat(paginationValues.totalEntries);
    };
    var handleLeftChevronClick = React.useCallback(function () {
        if (!getIsLeftChevronActive())
            return;
        var previousPage = currentPage - 1;
        setPagination(function (pagination) {
            var newPagination = __assign(__assign({}, pagination), { currentPage: previousPage, currentPageInput: previousPage.toString() });
            return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
        });
    }, [currentPage, pagination, setPagination]);
    var handleRightChevronClick = React.useCallback(function () {
        if (!getIsRightChevronActive())
            return;
        var nextPage = currentPage + 1;
        setPagination(function (pagination) {
            var newPagination = __assign(__assign({}, pagination), { currentPage: nextPage, currentPageInput: nextPage.toString() });
            return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
        });
    }, [currentPage, pagination, setPagination]);
    var handlePerPageInputChange = React.useCallback(function (event) {
        var sanitizedInputValue = removeAllNonDigitCharsAndLeadingZeroes(event.target.value);
        // max length of 2
        if (sanitizedInputValue.length >= 3) {
            sanitizedInputValue = sanitizedInputValue.substring(0, 2);
        }
        setPagination(function (pagination) {
            var newPagination = __assign(__assign({}, pagination), { numPerPageInput: sanitizedInputValue.toString() });
            return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
        });
    }, [pagination, setPagination]);
    var handleCurrentPageInputChange = React.useCallback(function (event) {
        var sanitizedInputValue = removeAllNonDigitCharsAndLeadingZeroes(event.target.value);
        // allow for empty string for keyboard users
        if (sanitizedInputValue === '') {
            setPagination(function (pagination) {
                var newPagination = __assign(__assign({}, pagination), { currentPageInput: '' });
                return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
            });
            return;
        }
        // otherwise only allow numbers within range
        var currentPageInputAsInt = parseInt(sanitizedInputValue);
        if (currentPageInputAsInt >= 1 && currentPageInputAsInt <= getNumPages()) {
            setPagination(function (pagination) {
                var newPagination = __assign(__assign({}, pagination), { currentPageInput: sanitizedInputValue.toString() });
                return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
            });
        }
    }, [pagination, setPagination]);
    var updatePaginationStateWithDataFromInputs = React.useCallback(function () {
        var numPerPageInputAsInt = parseInt("".concat(numPerPageInput));
        var currentPageInputAsInt = parseInt("".concat(currentPageInput));
        if (numPerPageInputAsInt !== numPerPage) {
            setPagination(function (pagination) {
                var newPagination = __assign(__assign({}, pagination), { currentPage: 1, currentPageInput: '1', numPerPage: numPerPageInputAsInt });
                return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
            });
        }
        if (currentPageInputAsInt !== currentPage) {
            setPagination(function (pagination) {
                var newPagination = __assign(__assign({}, pagination), { currentPage: currentPageInputAsInt });
                return __assign(__assign({}, newPagination), { text: getPaginationText(newPagination) });
            });
        }
    }, [pagination, setPagination, currentPage, numPerPage]);
    var handleKeyPress = function (event) {
        if (event.key === 'Enter') {
            updatePaginationStateWithDataFromInputs();
        }
    };
    // this object returned by usePagination should be set to the PaginationContextProvider's value prop.
    // ex: <PaginationContextProvider value={paginationInit}>, where value={paginationInit} is the value returned from this hook.
    // to share state across multiple components, all subsequent uses of this hook's state should use context, ex:
    // const {pagination, setPagination} = React.useContext(PaginationContext)
    return {
        getPaginationText: getPaginationText,
        goBackToPageOne: goBackToPageOne,
        handleCurrentPageInputChange: handleCurrentPageInputChange,
        handleKeyPress: handleKeyPress,
        handleLeftChevronClick: handleLeftChevronClick,
        handlePerPageInputChange: handlePerPageInputChange,
        handleRightChevronClick: handleRightChevronClick,
        isLeftChevronActive: getIsLeftChevronActive(),
        isRightChevronActive: getIsRightChevronActive(),
        numPages: getNumPages(),
        pagination: pagination,
        resetPagination: resetPagination,
        setPagination: setPagination,
        skip: currentPage > 1 ? (currentPage - 1) * numPerPage : 0,
        updatePaginationStateWithDataFromInputs: updatePaginationStateWithDataFromInputs,
    };
};

export { INITIAL_PAGINATION_STATE, NvPaginationContext, NvPaginationContextProvider, useNvPagination };
//# sourceMappingURL=useNvPagination.hooks.esm.js.map
